import React, { useEffect, useRef, useState } from "react";
import { ChevronsRight } from "lucide-react";

import { cn } from "../../../lib/utils";

const PANEL_WIDTH = 693;

interface WideSidePeekProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}

const WideSidePeek: React.FC<WideSidePeekProps> = ({
  title,
  subtitle,
  onClose,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));

    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!panelRef.current || panelRef.current.contains(target)) {
        return;
      }

      if (target.closest(".max-h-60") || target.closest(".z-\\[9999\\]")) {
        return;
      }

      onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      className={cn(
        `
          fixed right-0 z-[130]
          flex flex-col
          border-l border-[#E5E5E5] bg-white shadow-xl
          transition-transform duration-300 ease-in-out
        `,
        isVisible ? "translate-x-0" : "translate-x-full",
      )}
      style={{
        top: "var(--maintenance-banner-height, 0px)",
        height: "calc(100vh - var(--maintenance-banner-height, 0px))",
        width: PANEL_WIDTH,
      }}
    >
      <div className="flex h-[56px] shrink-0 items-center gap-3 border-b border-[#E5E5E5] bg-[#F9FAFB] px-4">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="flex h-5 w-5 items-center justify-center rounded transition-colors hover:bg-gray-100"
        >
          <ChevronsRight size={20} className="text-[#64748B]" />
        </button>

        <span className="truncate text-base font-medium leading-6 text-[#070B24]">
          {title}
          {subtitle && (
            <span className="font-normal text-[#737373]">{` | ${subtitle}`}</span>
          )}
        </span>
      </div>

      {children}
    </div>
  );
};

export default WideSidePeek;